<template>
  <div class="nav-container">
    <!-- 左右滚动按钮 -->
    <div
      v-if="isOverflow"
      class="scroll-btn toLeft"
      :class="{ disabled: scrollLeftDisabled }"
      @click="scrollNav('left')"
    >
      <div class="scroll-btn-inner">
        <i class="fas fa-chevron-left"></i>
      </div>
    </div>
    <div
      v-if="isOverflow"
      class="scroll-btn toRight"
      :class="{ disabled: scrollRightDisabled }"
      @click="scrollNav('right')"
    >
      <div class="scroll-btn-inner">
        <i class="fas fa-chevron-right"></i>
      </div>
    </div>

    <!-- 导航容器 -->
    <div class="nav-wrapper">
      <ul class="nav-bar" ref="scrollNav" @scroll="checkScrollability">
        <li
          v-for="(item, index) in list"
          :key="item[value]"
          :class="{ active: activeNav === index }"
          @click="setActiveNav(index, item)"
          class="nav-item"
        >
          <div class="nav-item-content">
            <span class="nav-text">{{ item[label] }}</span>
          </div>
          <div class="nav-indicator"></div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    list: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: "label"
    },
    value: {
      type: String,
      default: "value"
    },
    // 新增主题颜色属性
    themeColor: {
      type: String,
      default: "#3498db"
    }
  },
  data() {
    return {
      activeNav: 0,
      scrollPosition: 0,
      isOverflow: false
    };
  },
  computed: {
    scrollLeftDisabled() {
      return this.scrollPosition <= 0;
    },
    scrollRightDisabled() {
      const navElement = this.$refs.scrollNav;
      if (!navElement) return true;
      return this.scrollPosition >= navElement.scrollWidth - navElement.clientWidth;
    }
  },
  methods: {
    setActiveNav(index, item) {
      this.activeNav = index;
      this.$emit("change", { index, item });
    },
    scrollNav(direction) {
      const navElement = this.$refs.scrollNav;
      const scrollAmount = 200;

      if (direction === "left") {
        navElement.scrollLeft -= scrollAmount;
      } else {
        navElement.scrollLeft += scrollAmount;
      }

      // 更新滚动位置
      setTimeout(() => {
        this.scrollPosition = navElement.scrollLeft;
        this.checkScrollability();
      }, 300);
    },
    checkScrollability() {
      const navElement = this.$refs.scrollNav;
      if (!navElement) return;

      this.scrollPosition = navElement.scrollLeft;
    },
    checkOverflow() {
      const navElement = this.$refs.scrollNav;
      if (!navElement) return;

      this.isOverflow = navElement.scrollWidth > navElement.clientWidth;
    }
  },
  mounted() {
    const navElement = this.$refs.scrollNav;
    navElement.addEventListener("scroll", this.checkScrollability);

    // 检查是否需要显示滚动按钮
    this.$nextTick(() => {
      this.checkOverflow();
    });

    // 监听窗口大小变化
    window.addEventListener("resize", this.checkOverflow);
  },
  beforeDestroy() {
    const navElement = this.$refs.scrollNav;
    if (navElement) {
      navElement.removeEventListener("scroll", this.checkScrollability);
    }
    window.removeEventListener("resize", this.checkOverflow);
  },
  watch: {
    list() {
      this.$nextTick(() => {
        this.checkOverflow();
        this.checkScrollability();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.nav-container {
  margin: 0 auto;
  background: var(--color-primary-dark10);
  border-radius: 4px;
  padding: 0 50px;
  position: relative;

  &:hover {
    .scroll-btn {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
  }
}

/* 美化后的滚动按钮样式 */
.scroll-btn {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%) scale(0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  opacity: 0;
  z-index: 100;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8);

  /* 玻璃态效果 */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: linear-gradient(135deg, #ffffff 0%, #e9ecef 100%);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);

    .scroll-btn-inner {
      color: var(--color-primary);
      transform: scale(1.1);
    }
  }

  &:active {
    transform: translateY(-50%) scale(1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      transform: translateY(-50%) scale(0.9);
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1);

      .scroll-btn-inner {
        color: #6c757d;
        transform: scale(1);
      }
    }
  }
}

.scroll-btn-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  font-size: 14px;
  transition: all 0.3s ease;
  border-radius: 50%;
}

.toLeft {
  left: 12px;

  &:not(.disabled):hover {
    .scroll-btn-inner {
      transform: translateX(-1px) scale(1.1);
    }
  }
}

.toRight {
  right: 12px;

  &:not(.disabled):hover {
    .scroll-btn-inner {
      transform: translateX(1px) scale(1.1);
    }
  }
}

/* 添加脉冲动画效果 */
@keyframes pulse-glow {
  0% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1),
      0 0 0 3px rgba(var(--color-primary-rgb), 0.2);
  }
  100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
}

.scroll-btn:not(.disabled) {
  animation: pulse-glow 2s infinite;
}

.nav-wrapper {
  width: 100%;
  position: relative;
  height: 56px;
  overflow: hidden;
}

.nav-bar {
  display: flex;
  align-items: center;
  height: 100%;
  transition: all 0.3s ease;
  overflow-x: auto;
  scroll-behavior: smooth;
  list-style: none;
  padding: 0;
  margin: 0;

  /* 隐藏滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.nav-item {
  height: 100%;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  padding: 0 16px;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);

    .nav-text {
      transform: translateY(-1px);
    }
  }

  &.active {
    .nav-item-content {
      .nav-text {
        font-weight: 700;
        transform: translateY(-1px);
      }
    }

    .nav-indicator {
      opacity: 1;
      transform: translateX(-50%) scaleX(1);
    }
  }
}

.nav-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.nav-text {
  font-size: 15px;
  color: #fff;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.nav-indicator {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%) scaleX(-1);
  width: 60%;
  height: 3px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 2px;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .nav-container {
    width: 95%;
    padding: 0 40px;
  }

  .nav-bar {
    height: 52px;
  }

  .nav-item {
    min-width: 140px;
    padding: 0 12px;
  }

  .nav-text {
    font-size: 14px;
  }

  .scroll-btn {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 0 30px;
  }

  .nav-bar {
    height: 48px;
  }

  .nav-item {
    min-width: 120px;
    padding: 0 10px;
  }

  .nav-text {
    font-size: 13px;
  }

  .scroll-btn {
    width: 32px;
    height: 32px;

    &.toLeft {
      left: 8px;
    }

    &.toRight {
      right: 8px;
    }
  }

  .scroll-btn-inner {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .nav-container {
    padding: 0 20px;
  }

  .nav-bar {
    height: 44px;
  }

  .nav-item {
    min-width: 100px;
    padding: 0 8px;
  }

  .nav-item-content {
    gap: 6px;
  }

  .nav-text {
    font-size: 12px;
  }

  .scroll-btn {
    display: none; /* 在非常小的屏幕上隐藏按钮 */
  }
}

/* 动画效果 */
@keyframes navItemFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.nav-item {
  animation: navItemFadeIn 0.4s ease forwards;

  // 支持更多标签的动画延迟
  @for $i from 1 through 20 {
    &:nth-child(#{$i}) {
      animation-delay: #{$i * 0.05}s;
    }
  }
}

/* 深色主题适配 */
@media (prefers-color-scheme: dark) {
  .scroll-btn {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);

    .scroll-btn-inner {
      color: rgba(255, 255, 255, 0.8);
    }

    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);

      .scroll-btn-inner {
        color: white;
      }
    }
  }
}
</style>
